import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './AdminRoles.scss'
import { HorizontalLoader } from '../../../../../Components'
import adminManagementService from '../../../../../Services/adminManagementService'
import { ADMIN_SCOPE, Role } from '../../../../../Models/AdminManagement'
import RoleFormModal from '../Modals/RoleFormModal'
import Swal from 'sweetalert2'

// SUPER_ADMIN and DEVELOPER are special: the API hard-codes them as security scopes
const PROTECTED_ROLES: string[] = [ADMIN_SCOPE.SUPER_ADMIN, ADMIN_SCOPE.DEVELOPER]

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString() : '—')

const AdminRoles: React.FC = () => {
  const navigate = useNavigate()

  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)

  const fetchRoles = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      setRoles(await adminManagementService.getRoles())
    } catch (e) {
      setRoles([])
      setLoadError((e as Error)?.message || 'Failed to load roles.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRoles()
  }, [fetchRoles])

  const openCreate = () => {
    setEditingRole(null)
    setShowForm(true)
  }

  const openEdit = (role: Role) => {
    setEditingRole(role)
    setShowForm(true)
  }

  const handleSubmit = async (payload: { name: string; description?: string }) => {
    // Errors propagate to the modal, which renders them inline.
    if (editingRole) {
      await adminManagementService.updateRole(editingRole.id, payload)
    } else {
      await adminManagementService.createRole(payload)
    }
    await fetchRoles()
  }

  const handleDelete = (role: Role) => {
    Swal.fire({
      title: `Delete ${role.name}?`,
      text: 'Any administrator currently holding this role will lose it. This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e873a8',
      cancelButtonColor: '#82858a',
      confirmButtonText: 'Yes, Delete',
    }).then(async (result) => {
      if (!result.isConfirmed) return

      try {
        await adminManagementService.deleteRole(role.id)
        Swal.fire({
          icon: 'success',
          title: 'Deleted',
          text: `${role.name} has been removed.`,
          confirmButtonColor: '#e873a8',
        })
        fetchRoles()
      } catch (e) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: (e as Error)?.message || 'Failed to delete role.',
        })
      }
    })
  }

  return (
    <div className='AdminRoles'>
      <div className='container mt-4'>
        <div className='container'>
          <div className='card border-0'>
            <div className='card-header border-0 px-0'>
              <div className='row'>
                <div className='col-6 my-auto'>
                  <div className='title'>{roles.length} roles</div>
                </div>
                <div className='col-6 text-end'>
                  <button
                    type='button'
                    className='btn btn-reset me-2'
                    onClick={() => navigate('/admin-management')}
                  >
                    Administrators
                  </button>
                  <button type='button' className='btn btn-add-company' onClick={openCreate}>
                    Create Role
                  </button>
                </div>
              </div>
            </div>

            <div className='card-body'>
              {loading && <HorizontalLoader />}

              {loadError && (
                <div className='alert alert-danger d-flex justify-content-between align-items-center'>
                  <span>{loadError}</span>
                  <button type='button' className='btn btn-sm btn-reset' onClick={fetchRoles}>
                    Retry
                  </button>
                </div>
              )}

              <div className='table-responsive'>
                <table className='table'>
                  <thead>
                    <tr>
                      <td>Role</td>
                      <td>Description</td>
                      <td>Created</td>
                      <td></td>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.length > 0 ? (
                      roles.map((role) => {
                        const protectedRole = PROTECTED_ROLES.includes(role.name)

                        return (
                          <tr key={role.id}>
                            <td>
                              <span className='role-name'>{role.name}</span>
                              {protectedRole && <span className='protected-tag'>system</span>}
                            </td>
                            <td className='role-description'>
                              {role.description || <span className='text-muted'>—</span>}
                            </td>
                            <td>{formatDate(role.createdAt)}</td>
                            <td className='text-end'>
                              <button
                                type='button'
                                className='btn btn-sm btn-link-action'
                                onClick={() => openEdit(role)}
                              >
                                Edit
                              </button>
                              <button
                                type='button'
                                className='btn btn-sm btn-link-action text-danger'
                                disabled={protectedRole}
                                title={
                                  protectedRole
                                    ? 'This role is enforced by the API and cannot be deleted.'
                                    : undefined
                                }
                                onClick={() => handleDelete(role)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={4} className='text-center py-4 text-muted'>
                          {loading ? 'Loading…' : 'No roles defined yet'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RoleFormModal
        show={showForm}
        role={editingRole}
        onClose={() => {
          setShowForm(false)
          setEditingRole(null)
        }}
        onSubmit={handleSubmit}
      />
    </div>
  )
}

export default AdminRoles
